/**
 * SidebarProvider
 * 
 * Context provider for sidebar state management.
 * Simplifies state by separating mobile drawer and desktop sidebar concerns.
 */

import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useViewport } from '@/hooks/useViewport';
import { setGlobalCloseFunction } from '@/utils/closeSidebarDrawer';

const SidebarContext = createContext(null);

export const SidebarProvider = ({ children }) => {
  const viewport = useViewport();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    // Load from localStorage
    try {
      const stored = localStorage.getItem('sidebar-desktop-collapsed');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Determine viewport - handle undefined viewport gracefully
  const isDesktop = viewport?.isDesktop ?? (typeof window !== 'undefined' ? window.innerWidth >= 1024 : false);
  const isSmallScreen = viewport?.isSmallScreen ?? (typeof window !== 'undefined' ? window.innerWidth < 1024 : false);

  // Save desktop collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sidebar-desktop-collapsed', String(desktopCollapsed));
    } catch (error) {
      console.warn('Failed to save sidebar state:', error);
    }
  }, [desktopCollapsed]);

  // Close mobile drawer when switching to desktop (during render, not in effect)
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (isDesktop && mobileDrawerOpen) {
      setMobileDrawerOpen(false);
    }
  }

  // Global ESC key handler to close drawer from anywhere
  useEffect(() => {
    if (!mobileDrawerOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        setMobileDrawerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileDrawerOpen]);

  const closeMobileDrawer = () => setMobileDrawerOpen(false);
  const openMobileDrawer = () => setMobileDrawerOpen(true);

  // Expose close function globally for easy access
  useEffect(() => {
    setGlobalCloseFunction(closeMobileDrawer);
    return () => {
      setGlobalCloseFunction(null);
    };
  }, []);

  const value = useMemo(() => ({
    // Mobile drawer state
    mobileDrawerOpen,
    setMobileDrawerOpen,
    openMobileDrawer,
    closeMobileDrawer,
    
    // Desktop sidebar state
    desktopCollapsed,
    setDesktopCollapsed,
    toggleDesktopSidebar: () => setDesktopCollapsed(prev => !prev),
    
    // Viewport
    isDesktop,
    isSmallScreen,
  }), [mobileDrawerOpen, desktopCollapsed, isDesktop, isSmallScreen]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
};

